import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTableApi } from '~Components/TableUniverse';
import { useDrop } from 'react-dnd';
import PlusIcon from '~/Assets/icons/add.svg';
import SelectOptionModal from '~Components/SelectOptionModal';
import itemsType from '../dragItemsType';
import { range } from 'lodash';
import { useI18n, useLogger, useSelector } from '~/Hooks';
import { isSplitChequeCrashFixCompany, runDndDrop } from '~splitChequeCrashFix';

const Wrapper = styled.div<{
  isOver: boolean;
  canDrop: boolean;
}>`
  background: ${(props) => (props.isOver ? '#d5d7d8' : '#1c232c')};
  border-radius: 4px;
  height: 128px;
  width: 88px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.isOver ? '#1c232c' : '#9ea1a5')};
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  text-transform: uppercase;
  margin: 16px 0;
  box-sizing: border-box;
  position: relative;
  z-index: ${(props) => (props.canDrop ? 3 : 'auto')};
  transition: background 0.2s ease-in-out, color 0.2s ease-in-out;
`;

const AddIcon = styled.div`
  height: 24px;
  width: 24px;
  mask-image: url(${PlusIcon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  margin-bottom: 4px;
  background-color: #8b8f93;
`;

interface Props {
  tableID: string;
  nrOfCheques: number;
  selectCheque: (id: string) => void;
  enableShowCheckAnimation: () => void;
  tableCheques: any;
  showBong: () => void;
  showLogin: () => void;
  disableCoverage: boolean;
}

const NewCheque: React.FunctionComponent<Props> = ({
  tableID,
  nrOfCheques,
  selectCheque,
  enableShowCheckAnimation,
  tableCheques,
  showBong,
  showLogin,
  disableCoverage,
}) => {
  const { i18n } = useI18n();
  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff : false,
  );
  const [showModal, setShowModal] = useState(false);
  const [firstCheque, setFirstCheque] = useState(false);

  const logger = useLogger();
  const api = useTableApi();
  // only defer the drop mutation for the affected company
  const useFix = isSplitChequeCrashFixCompany(useSelector((state) => state.company));

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [itemsType.ITEM],
    drop: (data) => {
      // Defer the mutation until the dnd drop transaction has completed.
      // Mutating synchronously re-renders and unregisters drop targets
      // mid-transaction, crashing react-dnd with "Expected to find a valid target."
      runDndDrop(useFix, () => {
        //@ts-ignore
        api.moveItemsToNewCheque(data.selectedItems, tableID);
      });
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const createCheque = (coverage: number) => {
    const id = api.createCheque(tableID, coverage);
    api.log.createCheque(id, tableID, coverage);
    selectCheque(id);
    enableShowCheckAnimation();
    if (nrOfCheques === 0) {
      setFirstCheque(true);
    }
  };

  useEffect(() => {
    if (firstCheque) {
      showBong();
      setFirstCheque(false);
    }
  }, [firstCheque]);

  const handleClick = () => {
    if (staffId === null) {
      showLogin();
      return;
    }

    if (disableCoverage) {
      createCheque(0);
      return;
    }

    if (nrOfCheques < 1) {
      setShowModal(true);
    } else {
      const coverage = Math.max(
        ...tableCheques.map((item: any) => (item.coverage ? item.coverage : 0)),
      );

      logger.info('Creating new cheque');

      createCheque(coverage);
    }
  };

  const handleCoverage = (count: number) => {
    logger.info('Creating new cheque');
    createCheque(count);
    setShowModal(false);
  };

  return (
    <>
      <Wrapper onTouchStart={handleClick} ref={drop} canDrop={canDrop} isOver={isOver}>
        <AddIcon />
        {i18n('TM.New')}
      </Wrapper>
      {showModal && (
        <SelectOptionModal
          enableCustomInput
          title={i18n('TM.Coverage')}
          options={range(1, 8)}
          onSelect={(count) => {
            handleCoverage(count as number);
          }}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default NewCheque;
